/**
 * @flow
 *
 * This file contains shared helper methods for the authentication flows
 */
import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { GoogleAnalytics } from '../../services';
import { getApiConfig } from '../../config';
import { AgreementType } from 'fitbeat.models';
import { i18n, Logger } from '../../utils';
import { showMessage } from '../../ui';
import Logout from '../../utils/LogoutHelper';
import appleAuth, {
  appleAuthAndroid,
} from '@invertase/react-native-apple-authentication';
import { getUrls } from '../../config';
import { createPlatformUser } from './create-platform-user';

export const getAppleLoginCredentials = async (setBasicUserInformation) => {
  try {
    const appleAuthRequestResponse = await appleAuth.performRequest({
      requestedOperation: appleAuth.Operation.LOGIN,
      requestedScopes: [appleAuth.Scope.EMAIL, appleAuth.Scope.FULL_NAME],
    });

    if (appleAuthRequestResponse.fullName) {
      await setBasicUserInformation({
        firstname: appleAuthRequestResponse.fullName.givenName,
        lastname: appleAuthRequestResponse.fullName.familyName
      })
    }

    const { identityToken, nonce } = appleAuthRequestResponse;
    return auth.AppleAuthProvider.credential(identityToken, nonce);
  } catch (error) {
    if (error.code === "1000" || error.code === "1001") {
      Logger.debug("[SocialAuth] signin cancelled, exiting gracefully");
      return null;
    }
    throw error;
  }
}

export const getAppleAndroidLoginCredentials = async ()=>{
  try {
    const rawNonce = Math.round(Math.random(Date.now() + 1) * 10000000).toString();
    const randomNum = Math.round(Math.random(Date.now()) * 10000000);
    const { APPLE_SIGNIN_RETURN_URL } = getUrls();
  appleAuthAndroid.configure({
    clientId: 'fitbeat.web',
    redirectUri: APPLE_SIGNIN_RETURN_URL,
    responseType: appleAuthAndroid.ResponseType.ALL,
    scope: appleAuthAndroid.Scope.ALL,
    nonce: rawNonce,
    randomNum,
  })
  const response = await appleAuthAndroid.signIn();
  return auth.AppleAuthProvider.credential(response.id_token, rawNonce);
}  catch (error) {
  if (error.message === 'E_SIGNIN_CANCELLED_ERROR') {
    Logger.debug('[SocialAuth] signin cancelled, exiting gracefully');
    return null;
  }
  throw error;
}
}

export const handleOnCompleteSocialAuth = async (
  firebaseUser, basicUserInfo, navigate, type, setSignupError, deeplinkSignupParams, provider) => {
  const googleAnalytics = new GoogleAnalytics();
  const userServiceClient = new UserServiceClient(getApiConfig(), fetch, Logger.debug, new AuthTokenBuilder(), googleAnalytics);
  try {
    const user = firebaseUser.user;
    const userDetails = user._user ? user._user : user;
    const providerDataEmail = userDetails.providerData && userDetails.providerData[0] ? userDetails.providerData[0].email : null;
    userDetails.email = userDetails.email ? userDetails.email : providerDataEmail;
    Logger.debug('[SocialAuth/handleOnCompleteSocialAuth]', { userDetails });
    if (!userDetails.email) {
      throw new Error(i18n.t('auth:socialAccountHasNotEmailError'));
    }
    const { additionalUserInfo: { isNewUser } } = firebaseUser;

    const allowedToContinueAuthFlow = await validateUserIsAllowedToContinueAuthFlow(userServiceClient, type, isNewUser);
    Logger.debug('[SocialAuth/handleOnCompleteSocialAuth]', { allowedToContinueAuthFlow });
    if (!allowedToContinueAuthFlow.canContinue) {
      Logger.debug('[SocialAuth/handleOnCompleteSocialAuth] canContinue is false');
      setSignupError(allowedToContinueAuthFlow.reason);
      Logout();
      return navigate('Welcome');
    }

    const existingUser = await userServiceClient.getAuthenticatedUser();
    const hasRequiredInfo = UserProfile.hasRequiredSignupDetails(existingUser);
    if (!hasRequiredInfo) {
      const updatedProfile = {
        ...existingUser,
        ...basicUserInfo,
      }
      Logger.debug('[SocialAuth/handleOnCompleteSocialAuth]', { updatedProfile });
      await userServiceClient.updateUser(updatedProfile);
    }

    if (!isNewUser && type === 'signUp') {
      showMessage(i18n.t('auth:accountExistsSocialError'));
    }

    Logger.debug('[SocialAuth/handleOnCompleteSocialAuth] done');
    navigate('AuthLoading');
  } catch (error) {
    if (error.message.indexOf('No user found') === -1) {
      throw error;
    }

    const userProfile = await createUser(firebaseUser.user, basicUserInfo, deeplinkSignupParams, provider);
    navigate('HomeNavigator', {
      userProfile,
    });
  }
}

export const getAuthenticatedUser = async () => {
  const userServiceClient = new UserServiceClient(getApiConfig(), fetch, Logger.debug, new AuthTokenBuilder(), new GoogleAnalytics());
  return userServiceClient.getAuthenticatedUser();
}

const createUser = async (firebaseUser, basicUserInfo, deeplinkSignupParams, provider = 'unknown') => {
  const googleAnalytics = new GoogleAnalytics();
  const userDetails = firebaseUser._user ? firebaseUser._user : firebaseUser;
  const [firstname, lastname] = extractNameFromDisplayName(userDetails.displayName);

  const userProfile = await createPlatformUser(
    {
      uid: userDetails.uid,
      email: userDetails.email,
      firstname,
      lastname,
      ...basicUserInfo,
    },
    deeplinkSignupParams,
    {
      shouldAddLegalAgreements: false,
    }
  )
  if (userDetails.providerData.length !== 0) {
    provider = userDetails.providerData[0].providerId;
  }
  googleAnalytics.setUserId(userDetails.uid);
  googleAnalytics.logEventWithParamaters('sign_up_social_complete_profile', { method: provider });
  return userProfile;
}

const extractNameFromDisplayName = (displayName) => {
  displayName = displayName || '';
  const parts = displayName.split(' ');
  const firstName = parts.splice(0, 1);
  return [firstName[0], parts.join(' ')];
}

export const appSignupAgreementSet = [
  AgreementType.Consolidated,
];

export const addLegalAgreements = (userProfile) => {
  userProfile.legalAgreements = appSignupAgreementSet;
  if (UserProfile.isMinor(userProfile.dateOfBirth)) {
    userProfile.guardianInfo.legalAgreements = appSignupAgreementSet;
  }
}

const validateUserIsAllowedToContinueAuthFlow = async (userServiceClient, authType, isNewUser) => {
  try {
    await userServiceClient.validateUserIsAllowedToContinueAuthFlow(authType, isNewUser);
    return {
      canContinue: true,
    };
  } catch (e) {
    Logger.debug(`[SocialAuth/validateUserIsAllowedToContinueAuthFlow] failed`, e.message);
    return {
      canContinue: false,
      reason: e.message,
    };
  }
}

export const getAuthProvider = (currentUser) => {
  let provider = 'unknown';
  if (currentUser.providerData.length !== 0) {
    provider = currentUser.providerData[0].providerId;
  }
  return provider;
};

export function cycleEnumValues(enumObj, defaultIndex) {
  const enumValues = Object.values(enumObj);
  let currentIndex = defaultIndex;
  return function getNextEnumValue() {
    const nextIndex = (currentIndex + 1) % enumValues.length;
    const value = enumValues[nextIndex];
    currentIndex = nextIndex;
    return value;
  };
}
